import { Tab, TabList, TabPanel, TabProvider } from "@ariakit/react";
import { TarReader } from "@gera2ld/tarjs";
import { PencilSimple } from "@phosphor-icons/react";
import { Button, NativeSelect } from "@replicate/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { parse } from "yaml";
import type { Model } from "../../types";
import { route } from "../../urls";
import CodeBlock from "../code-block";

function useModelSource({
  username,
  name,
  id,
}: {
  username: string;
  name: string;
  id: string;
}) {
  return useQuery({
    queryFn: async () => {
      const response = await fetch(
        route("api_model_source", {
          username,
          name,
          id,
        })
      );

      if (!response.ok) {
        throw new Error(`Error fetching model source: ${response.statusText}`);
      }

      const tarData = await response.arrayBuffer();
      const reader = await TarReader.load(tarData);

      const source: Record<string, string> = {};
      for await (const entry of reader.fileInfos) {
        const content = reader.getTextFile(entry.name);
        source[entry.name] = content;
      }

      let entryPoint = "main.py";

      try {
        const parsedCogYaml = parse(source["cog.yaml"]) as {
          predict: string;
        };
        if (parsedCogYaml.predict) {
          const [entry, _] = parsedCogYaml.predict.split(":");
          if (entry) {
            entryPoint = entry;
          }
        }
      } catch (e) {
        console.error("Failed to parse cog.yaml:", e);
      }

      return {
        source,
        entryPoint,
      };
    },
    queryKey: ["model-source", name, id],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

function ModelSourcePlaceholder() {
  return (
    <div className="border border-r8-gray-6">
      <div className="relative flex-1 min-w-0 z-0">
        <div
          aria-hidden
          className="absolute pointer-events-none inset-0 size-full bg-gradient-to-b dark:from-black/30 dark:to-black/50 from-white/30 to-white/90"
        />
        <div className="absolute inset-0 size-full flex items-center justify-center px-8 z-20">
          <div className="flex flex-col items-center gap-2">
            <h2 className="font-heading text-r8-xl font-semibold">
              Source unavailable
            </h2>
            <p className="max-w-lg text-center mx-auto">
              The source code for this model is not available.
            </p>
          </div>
        </div>
        <div className="min-w-0 h-96 p-4 flex flex-col gap-4 bg-r8-gray-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i}>
              <div
                className="h-3 bg-r8-gray-4 dark:bg-r8-gray-3"
                style={{ width: `${Math.random() * 60 + 20}%` }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ModelSource({
  id,
  model,
}: { id: string | null; model: Model }) {
  if (!id) return <ModelSourcePlaceholder />;
  return <ModelSourceInner id={id} model={model} />;
}

export function ModelSourceInner({ id, model }: { id: string; model: Model }) {
  const { data, isLoading, isSuccess, error } = useModelSource({
    username: model.owner,
    name: model.name,
    id,
  });

  if (isLoading) {
    return <div>Loading model source…</div>;
  }

  if (error) {
    return <div>Error loading model source: {error.message}</div>;
  }

  if (isSuccess) {
    return (
      <SourceViewer
        model={model}
        source={data.source}
        entryPoint={data.entryPoint}
      />
    );
  }

  return null;
}

function SourceViewer({
  entryPoint,
  model,
  source,
}: { entryPoint: string; model: Model; source: Record<string, string> }) {
  const [selectedFile, setSelectedFile] = useState<string>(entryPoint);

  return (
    <div className="flex flex-col-reverse md:flex-row gap-4">
      <div className="flex-1 min-w-0">
        <TabProvider
          orientation="vertical"
          selectedId={selectedFile}
          setSelectedId={(id) => {
            if (!id) return;
            setSelectedFile(id);
          }}
        >
          <div className="md:border border-r8-gray-6 flex flex-col gap-4 md:gap-0 md:flex-row md:divide-x divide-r8-gray-6">
            <div className="md:hidden">
              <NativeSelect
                name="model-source-select"
                id="model-source-select"
                value={selectedFile}
                onChange={(e) => {
                  setSelectedFile(e.target.value);
                }}
              >
                {Object.keys(source).map((filename) => {
                  return (
                    <option value={filename} key={filename}>
                      {filename}
                    </option>
                  );
                })}
              </NativeSelect>
            </div>
            <aside className="hidden md:block w-64 shrink-0">
              <TabList className="flex flex-col p-2">
                {Object.keys(source).map((filename) => {
                  return (
                    <Tab
                      id={filename}
                      className="text-left text-r8-sm p-2 hover:bg-r8-gray-a2 focus:outline-none aria-selected:bg-r8-gray-2 aria-selected:text-r8-gray-12 border-r-2 border-transparent aria-selected:border-r8-blue-10"
                      key={filename}
                    >
                      {filename}
                    </Tab>
                  );
                })}
              </TabList>
            </aside>
            <div className="flex-1 overflow-hidden min-w-0 border border-r8-gray-6 md:border-0">
              {Object.entries(source).map(([filename, content]) => {
                return (
                  <TabPanel
                    tabId={filename}
                    className="min-h-[400px] procedure-source h-full flex flex-col"
                    key={filename}
                  >
                    <CodeBlock
                      copyable={false}
                      showLineNumbers
                      textContent={content}
                      copyContent={content}
                      language="python"
                    />
                  </TabPanel>
                );
              })}
            </div>
          </div>
        </TabProvider>
      </div>
      <div className="w-48 shrink-0 min-w-0">
        <Button
          size="sm"
          variant="clear"
          className="w-full justify-start"
          render={
            // biome-ignore lint/a11y/useAnchorContent: Content injected
            <a
              href={`https://replicate.com/editor/${model.owner}/${model.name}`}
            />
          }
          startIcon={<PencilSimple />}
        >
          Edit model
        </Button>
      </div>
    </div>
  );
}
